import {Component, OnInit, Output, EventEmitter, Input} from '@angular/core';
import {Donation} from "../donation/donation";
import {ShopCartService} from "../shop/shop.cart.service";
import {Product} from "../shop/shop.product.model";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {OptionService} from "../option/option.service";
import {ContactComponent} from "../contact/contact.component";
import {ShopCartItem} from "../shop/shop.cart.item";
import {DecimalPipe} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";

declare let __twingle_giftUrl;

@Component({
  selector: 'giftshop-cart',
  templateUrl: './giftshop.cart.component.html',
  providers: [DecimalPipe]
})
export class GiftshopCartComponent implements OnInit {

  /**
   * The current donation
   */
  @Input() donation: Donation;

  /**
   * current widgetsize
   */
  @Input() size: string;

  /**
   * formgroup for validation of the input
   */
  @Input() form: FormGroup;

  /**
   * Output parameter for changing the step
   * @type {EventEmitter}
   */
  @Output() stepChange: EventEmitter<string> = new EventEmitter();

  /**
   * List of items to be shown in the cart overview.
   */
  items: ShopCartItem[];

  /**
   * the overall price of the whole cart.
   */
  overallPrice = 0;

  /**
   * indicator if the form is valid or not.
   */
  formValid: boolean;

  /**
   * are the contact data mandatory or not
   */
  contactMandatory = false;

  /**
   * URL for the example image
   */
  exampleImage: string;

  /**
   * toggle for hinding the sender field
   */
  hideSender = false;

  /**
   * toggle for hiding the message field
   */
  hideMessage = false;

  /**
   * holds a marker if an image is currently loaded or not to show a loading animation.
   */
  imageLoad: { url: string, load: boolean }[] = [];

  /**
   * constructor
   * @param cartService
   * @param optionService
   * @param decimalPipe
   * @param translate
   */
  constructor(private cartService: ShopCartService, private optionService: OptionService,
              private decimalPipe: DecimalPipe, private translate: TranslateService) {
  }

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // check if the contact data should be requested manually
    this.contactMandatory = ContactComponent.isContactMandatory();

    // check if we should hide some fields
    this.hideSender = this.optionService.getOption('has_hidden_sender') ?? false;
    this.hideMessage = this.optionService.getOption('has_hidden_message') ?? false;

    // get the items from the cart
    this.items = this.cartService.getItems();

    // check if we can preset message and sender
    if (this.items.length > 0 && (this.items[0].giftSender || this.items[0].giftMessage) && this.items.length > 1) {
      for (let i = 1; i < this.items.length; i++) {
        this.items[i].giftSender = this.items[i].giftSender ? this.items[i].giftSender : this.items[0].giftSender;
        this.items[i].giftMessage = this.items[i].giftMessage ? this.items[i].giftMessage : this.items[0].giftMessage;
      }
    }

    // update items if the count is changed
    this.cartService.itemsUpdated.subscribe(() => this.updateItems());
    this.updateItems();

    // init toggle if the button is actiove or not
    this.formValid = this.overallPrice > 0;

    // URL for the preview image
    if (typeof __twingle_giftUrl !== "undefined") {
      this.exampleImage = __twingle_giftUrl;
    }

    // add validators for every product
    for (let i = 0; i < this.items.length; i++) {
      this.form.addControl('gift.receiver.' + i, new FormControl(null, [Validators.required]));
      if (this.hideSender === false) {
        this.form.addControl('gift.sender.' + i, new FormControl(null, [Validators.required]));
      }
      if (this.hideMessage === false) {
        this.form.addControl('gift.message.' + i, new FormControl(null, [Validators.required]));
      }
    }

    // subscribe for form changes.
    this.form.valueChanges.subscribe(() => this.onFormChanged());

    // initially set preview URL
    for (let i = 0; i < this.items.length; i++) {
      this.updatePreviewUrl(i);
    }
  }

  /**
   * eventhandler when the validation of the form changed.
   */
  onFormChanged(): void {
    // update the data of the products in the donation.
    this.cartService.updateProductsInDonation();

    // set this via timeout, otherwise the changedetection throws errors.
    setTimeout(() => {
      this.formValid = this.overallPrice > 0 && this.form.valid === true;
    }, 0)
  }

  /**
   * Updates the items and the overall price
   */
  updateItems(): void {
    this.overallPrice = this.cartService.getPrice();
    this.items = this.cartService.getItems();
  }

  /**
   * Updates the preview URL for a given item index.
   * @param index
   */
  updatePreviewUrl(index: number): void {
    if (!this.exampleImage || !this.items[index]) {
      // do nothing if we do not have a baseURL
      return;
    }

    // get the users locale
    const currentLocale = this.translate.currentLang;

    if (!this.imageLoad[index]) {
      this.imageLoad[index] = {url: null, load: true};
    }
    let url = this.exampleImage;
    url += '/' + currentLocale;
    url += '?i=' + this.items[index].product.id;
    url += '&c=' + this.items[index].count;
    if (this.items[index].freePrice) {
      url += '&a=' + this.items[index].freePrice;
    }
    if (this.items[index].giftReceiver) {
      url += '&r=' + encodeURIComponent(this.items[index].giftReceiver);
    }
    if (this.items[index].giftSender) {
      url += '&s=' + encodeURIComponent(this.items[index].giftSender);
    }
    if (this.items[index].giftMessage) {
      url += '&m=' + encodeURIComponent(this.items[index].giftMessage);
    }
    if (this.imageLoad[index].url !== url) {
      this.imageLoad[index].load = true;
    }
    this.imageLoad[index].url = url;
  }

  /**
   * Add another item to the cart
   */
  addItem(): void {
    this.stepChange.emit('list');
  }

  /**
   * show the payment step
   */
  nextStep(): void {
    if (this.overallPrice > 0 && this.formValid) {
      if (this.contactMandatory === true) {
        this.stepChange.emit('contact-mandatory');
      } else {
        this.stepChange.emit(this.size === 'small' ? 'payment-select' : 'payment');
      }
    } else {
      // form is not valid
      for (const name of Object.keys(this.form.controls)) {
        (<FormControl>this.form.controls[name]).markAsTouched({onlySelf: true});
      }

      // scroll to the first invalid element
      document.querySelectorAll('.ng-invalid')[0].scrollIntoView();
    }
  }

  /**
   * increase the count for a single product
   * @param productIndex
   * @param currentCount
   */
  increaseCount(productIndex: number, currentCount: number): void {
    this.cartService.setItemCountByIndex(productIndex, currentCount + 1);
    this.items = this.cartService.getItems();
    this.updatePreviewUrl(productIndex);
  }

  /**
   * decrease the count for a single product
   * @param productIndex
   * @param currentCount
   */
  decreaseCount(productIndex: number, currentCount: number): void {
    if (currentCount > 1) {
      this.cartService.setItemCountByIndex(productIndex, currentCount - 1);
      this.items = this.cartService.getItems();
      this.updatePreviewUrl(productIndex);
    }
  }

  /**
   * setts the count of s single product to a fixed value
   * @param product
   * @param count
   */
  setCount(product: Product, count: number): void {
    if (count > 0) {
      this.cartService.setItemCount(product, count);
    }
  }

  /**
   * removes the items from the cart.
   * @param productIndex
   */
  removeItem(productIndex: number): void {
    this.cartService.removeItemByIndex(productIndex);

    // remove the control elements from the form
    this.form.removeControl('gift.receiver.' + productIndex);
    if (this.hideSender === false) {
      this.form.removeControl('gift.sender.' + productIndex);
    }
    if (this.hideMessage === false) {
      this.form.removeControl('gift.message.' + productIndex);
    }

    if (this.imageLoad[productIndex]) {
      this.imageLoad.splice(productIndex, 1);
    }
  }

  /**
   * returns the path to the product image.
   * @param product
   */
  getProuctImage(product: Product): string {
    if (product.images.length > 0) {
      return this.optionService.getImageDirectory() + '/' + product.images[0].file;
    }
    return null;
  }

  /**
   * Returns a list of placeholders that we need inside the last submit button in the widget
   */
  getSubmitButtonPlaceholders(): any {
    // get the textkey for the rythm placeholder
    let rythmPlaceholderTextkey = 'rythm.one_time.placeholder';
    if (this.donation.recurringRythm) {
      rythmPlaceholderTextkey = 'rythm.recurring.' + this.donation.recurringRythm + '.placeholder';
    }

    // if we have a membership widget, we calculate the value, based on the selected rythm
    let membershipAmount = this.donation.amount;
    if (this.donation.recurringRythm) {
      switch (this.donation.recurringRythm) {
        case 'yearly':
          membershipAmount *= 12;
          break;
        case 'halfyearly':
          membershipAmount *= 6;
          break;
        case 'quarterly':
          membershipAmount *= 3;
          break;
      }
    }

    // format amounts
    let amount: string = this.decimalPipe.transform(this.donation.amount);
    let membershipAmountPlaceholder: string = this.decimalPipe.transform(membershipAmount);

    // add extra amount if selected
    if (this.donation.extraAmount > 0) {
      amount += ' + ' + this.donation.extraAmount;
      membershipAmountPlaceholder += ' + ' + this.donation.extraAmount;
    }

    return {
      amount: amount,
      membershipAmount: membershipAmountPlaceholder,
      currencySymbol: this.donation.currencySymbol,
      rythm: this.translate.instant(rythmPlaceholderTextkey)
    };
  }
}
